import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface RefreshIntervals {
  devices: number;
  status: number;
  anomalies: number;
  discovery: number;
  assets: number;
}

export interface Settings {
  refreshIntervals: RefreshIntervals;
  theme: 'light' | 'dark' | 'system';
  compactView: boolean;
  showInactiveDevices: boolean;
  autoDiscoveryEnabled: boolean;
  notificationsEnabled: boolean;
  anomaliesLimit: number;
  inactivityTimeoutHours: number;
}

interface SettingsContextType {
  settings: Settings;
  updateRefreshInterval: (key: keyof RefreshIntervals, value: number) => void;
  updateSetting: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  resetSettings: () => void;
}

const defaultSettings: Settings = {
  refreshIntervals: {
    devices: parseInt(process.env.NEXT_PUBLIC_DEVICES_POLLING_INTERVAL || '600000'), // 10 minutes - cache duration for device data
    status: parseInt(process.env.NEXT_PUBLIC_SYSTEM_STATUS_POLLING_INTERVAL || '600000'), // 10 minutes - cache duration for system status
    anomalies: parseInt(process.env.NEXT_PUBLIC_ANOMALIES_POLLING_INTERVAL || '120000'), // 2 minutes - anomalies need fresher data
    discovery: parseInt(process.env.NEXT_PUBLIC_DEVICES_POLLING_INTERVAL || '600000'), // 10 minutes - cache duration for discovery
    assets: parseInt(process.env.NEXT_PUBLIC_ASSETS_POLLING_INTERVAL || '600000'), // 10 minutes - cache duration for assets
  },
  theme: 'light',
  compactView: false,
  showInactiveDevices: true,
  autoDiscoveryEnabled: true,
  notificationsEnabled: true,
  anomaliesLimit: 25, // Increased from 5 to show more critical issues on dashboard
  inactivityTimeoutHours: 24, // 24 hours - default timeout for device inactivity detection
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const STORAGE_KEY = 'struvital-settings';

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          return { ...defaultSettings, ...JSON.parse(stored) };
        } catch (e) {
          console.error('Failed to parse stored settings:', e);
        }
      }
    }
    return defaultSettings;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const updateRefreshInterval = (key: keyof RefreshIntervals, value: number) => {
    setSettings(prev => ({
      ...prev,
      refreshIntervals: {
        ...prev.refreshIntervals,
        [key]: value,
      },
    }));
  };

  const updateSetting = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        updateRefreshInterval,
        updateSetting,
        resetSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}